import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { ShieldCheck, ShieldOff, Loader2, Smartphone, MessageSquare } from "lucide-react";
import api from "../../api";
import { setUser } from "../../store/slices/authSlice";

/**
 * TwoFactorSettings lets an agent / admin / PA turn two-factor authentication
 * on or off. Rendered on the Security tab in Settings.
 *
 * There are two ways to receive the second factor and the user picks one:
 *   Text message      (enter phone if none) -> text a code -> confirm code -> on
 *   Authenticator app scan a QR code -> type the code the app shows -> on
 *     (Google Authenticator, Microsoft Authenticator, Authy, 1Password...)
 *
 * Disable flow, either way: confirm account password -> off.
 */
const TwoFactorSettings = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState({
    enabled: false,
    has_phone: false,
    phone_masked: null,
    method: "sms",
    totp_configured: false,
  });
  // idle | choose | phone | code | totp | disable
  const [step, setStep] = useState("idle");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  // The QR image, the otpauth URI and the typed-in fallback secret, from setup.
  const [totpSetup, setTotpSetup] = useState(null);

  const usingApp = status.enabled && status.method === "totp" && status.totp_configured;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("role-management/2fa/status/");
        if (!cancelled) setStatus(res.data || {});
      } catch (err) {
        console.error("Failed to load 2FA status", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep the redux/localStorage user in sync so the login nudge reflects reality.
  const syncUserEnabled = (enabled) => {
    if (user) dispatch(setUser({ ...user, two_factor_enabled: enabled }));
  };

  const sendCode = async (phoneNumber) => {
    setBusy(true);
    try {
      const res = await api.post("role-management/2fa/send-code/", {
        ...(phoneNumber ? { phone_number: phoneNumber } : {}),
      });
      setStatus((s) => ({ ...s, has_phone: true, phone_masked: res.data?.phone_masked || s.phone_masked }));
      setStep("code");
      setOtp("");
      toast.success(res.data?.detail || "Verification code sent.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send the code. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const startSms = () => {
    if (status.has_phone) {
      sendCode();
    } else {
      setPhone(user?.phone_number || "");
      setStep("phone");
    }
  };

  // Mints a brand new secret every time, so an abandoned half-scan is never
  // left live on the account. The QR is only shown after this succeeds.
  const startAuthenticatorApp = async () => {
    setBusy(true);
    try {
      const res = await api.post("role-management/2fa/totp/setup/");
      setTotpSetup(res.data || {});
      setOtp("");
      setStep("totp");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start setup. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitPhone = (e) => {
    e.preventDefault();
    if (!phone.trim()) {
      toast.error("Enter your mobile number.");
      return;
    }
    sendCode(phone.trim());
  };

  const handleConfirmCode = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp.trim())) {
      toast.error("Enter the 6-digit code.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("role-management/2fa/enable/", { otp: otp.trim() });
      setStatus((s) => ({
        ...s,
        enabled: true,
        has_phone: true,
        phone_masked: res.data?.phone_masked,
        method: "sms",
      }));
      syncUserEnabled(true);
      setStep("idle");
      setOtp("");
      toast.success(res.data?.detail || "Two-factor authentication is on.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't verify that code.");
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmAppCode = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp.trim())) {
      toast.error("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("role-management/2fa/totp/enable/", { code: otp.trim() });
      setStatus((s) => ({ ...s, enabled: true, method: "totp", totp_configured: true }));
      syncUserEnabled(true);
      setStep("idle");
      setOtp("");
      setTotpSetup(null);
      toast.success(res.data?.detail || "Two-factor authentication is on.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't verify that code.");
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Enter your password to confirm.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("role-management/2fa/disable/", { password });
      setStatus((s) => ({ ...s, enabled: false, totp_configured: false }));
      syncUserEnabled(false);
      setStep("idle");
      setPassword("");
      toast.success(res.data?.detail || "Two-factor authentication is off.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't turn off 2FA.");
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setStep("idle");
    setOtp("");
    setPassword("");
    setTotpSetup(null);
  };

  const inputClass =
    "w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent";
  const codeInputClass = `${inputClass} text-center text-xl tracking-[0.4em] font-semibold`;
  const primaryBtn =
    "px-4 py-2 bg-[#55C5D0] text-white rounded-lg text-sm font-medium hover:bg-[#3bb3be] disabled:opacity-50";
  const linkBtn = "text-[#55C5D0] text-sm font-medium hover:underline disabled:opacity-50";

  // The two ways in, shown side by side so neither looks like the only option.
  const methodChooser = (
    <div className="grid gap-3 sm:grid-cols-2">
      <button
        type="button"
        onClick={startSms}
        disabled={busy}
        className="text-left border border-gray-200 rounded-xl p-4 hover:border-[#55C5D0] hover:bg-[#55C5D0]/5 disabled:opacity-50 transition"
      >
        <div className="flex items-center gap-2 mb-1">
          <MessageSquare className="w-4 h-4 text-[#55C5D0]" />
          <span className="font-medium text-gray-800 text-sm">Text message</span>
        </div>
        <p className="text-xs text-gray-500">
          We text a 6-digit code to your mobile each time you sign in.
        </p>
      </button>

      <button
        type="button"
        onClick={startAuthenticatorApp}
        disabled={busy}
        className="text-left border border-gray-200 rounded-xl p-4 hover:border-[#55C5D0] hover:bg-[#55C5D0]/5 disabled:opacity-50 transition"
      >
        <div className="flex items-center gap-2 mb-1">
          <Smartphone className="w-4 h-4 text-[#55C5D0]" />
          <span className="font-medium text-gray-800 text-sm">Authenticator app</span>
        </div>
        <p className="text-xs text-gray-500">
          Google Authenticator, Microsoft Authenticator, Authy or 1Password. Works with
          no phone signal.
        </p>
      </button>
    </div>
  );

  return (
    <section id="two-factor" className="bg-white rounded-2xl shadow border border-gray-100 p-6">
      <div className="flex items-center mb-4">
        {status.enabled ? (
          <ShieldCheck className="w-5 h-5 text-emerald-500 mr-2" />
        ) : (
          <ShieldOff className="w-5 h-5 text-gray-400 mr-2" />
        )}
        <h3 className="text-lg font-semibold text-gray-800">Two-Factor Authentication</h3>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-600 mb-4">
            Add an extra layer of security. When on, you'll enter a 6-digit code each time
            you sign in, from a text message or from an authenticator app, whichever you pick.
          </p>

          {/* Current state banner */}
          <div
            className={`flex items-center justify-between gap-3 rounded-xl px-4 py-3 mb-4 text-sm ${
              status.enabled ? "bg-emerald-50 text-emerald-800" : "bg-gray-50 text-gray-600"
            }`}
          >
            <span>
              {status.enabled ? (
                usingApp ? (
                  <>
                    <strong>On</strong>, using your authenticator app.
                  </>
                ) : (
                  <>
                    <strong>On</strong>, codes are sent to {status.phone_masked || "your mobile"}.
                  </>
                )
              ) : (
                <>
                  <strong>Off</strong>, your account is protected by password only.
                </>
              )}
            </span>
          </div>

          {/* Idle actions */}
          {step === "idle" && (
            <div>
              {status.enabled ? (
                <div className="flex flex-wrap gap-2 items-center">
                  <button
                    type="button"
                    onClick={() => setStep("disable")}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
                  >
                    Turn off
                  </button>
                  {/* Switching method does not go through "turn off" first: making
                      somebody drop to password-only in between is worse security
                      than letting them re-verify straight into the other method. */}
                  {usingApp ? (
                    <button type="button" onClick={startSms} disabled={busy} className={linkBtn}>
                      Use text messages instead
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={startAuthenticatorApp}
                      disabled={busy}
                      className={linkBtn}
                    >
                      Use an authenticator app instead
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Choose how you want to get your codes
                  </p>
                  {methodChooser}
                </>
              )}
            </div>
          )}

          {/* Phone entry */}
          {step === "phone" && (
            <form onSubmit={handleSubmitPhone} className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">Mobile number</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="04xx xxx xxx"
                className={inputClass}
                autoFocus
              />
              <div className="flex gap-2">
                <button type="submit" disabled={busy} className={primaryBtn}>
                  {busy ? "Sending…" : "Send code"}
                </button>
                <button type="button" onClick={cancel} className="px-4 py-2 text-gray-500 text-sm hover:text-gray-700">
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Code confirmation (text message) */}
          {step === "code" && (
            <form onSubmit={handleConfirmCode} className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Enter the 6-digit code sent to {status.phone_masked || "your mobile"}
              </label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="••••••"
                className={codeInputClass}
                autoFocus
              />
              <div className="flex gap-2 items-center">
                <button type="submit" disabled={busy} className={primaryBtn}>
                  {busy ? "Verifying…" : "Verify & enable"}
                </button>
                <button type="button" onClick={() => sendCode()} disabled={busy} className={linkBtn}>
                  Resend
                </button>
                <button type="button" onClick={cancel} className="ml-auto px-3 py-2 text-gray-500 text-sm hover:text-gray-700">
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Authenticator app setup: scan, then prove it worked */}
          {step === "totp" && (
            <form onSubmit={handleConfirmAppCode} className="space-y-4">
              <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                <li>Open your authenticator app and choose to add an account.</li>
                <li>Scan the QR code below.</li>
                <li>Enter the 6-digit code the app shows.</li>
              </ol>

              <div className="flex flex-col sm:flex-row gap-4 items-start">
                {totpSetup?.qr_code ? (
                  <img
                    src={totpSetup.qr_code}
                    alt="QR code for your authenticator app"
                    className="w-40 h-40 border border-gray-200 rounded-lg bg-white p-2 shrink-0"
                  />
                ) : null}
                <div className="text-sm text-gray-600">
                  {/* Printed as well as drawn: you cannot scan a QR code with the
                      same phone that is displaying it, and some apps have no camera
                      permission. Typing the key in by hand is the way round both. */}
                  <p className="mb-1 font-medium text-gray-700">Can't scan it?</p>
                  <p className="mb-2">Enter this key in your app by hand:</p>
                  <code className="block bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 font-mono text-xs tracking-wider break-all">
                    {totpSetup?.secret || "…"}
                  </code>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Code from your authenticator app
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="••••••"
                  className={codeInputClass}
                  autoFocus
                />
              </div>

              <div className="flex gap-2 items-center">
                <button type="submit" disabled={busy} className={primaryBtn}>
                  {busy ? "Verifying…" : "Verify & enable"}
                </button>
                <button type="button" onClick={cancel} className="ml-auto px-3 py-2 text-gray-500 text-sm hover:text-gray-700">
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Disable confirmation */}
          {step === "disable" && (
            <form onSubmit={handleDisable} className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Enter your password to turn off two-factor authentication
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                className={inputClass}
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={busy}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-50"
                >
                  {busy ? "Turning off…" : "Turn off 2FA"}
                </button>
                <button type="button" onClick={cancel} className="px-4 py-2 text-gray-500 text-sm hover:text-gray-700">
                  Cancel
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </section>
  );
};

export default TwoFactorSettings;
